import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { caseActionPath, CASE_ACTION_HASH } from "@/app/routes";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { notifySuccess } from "@/lib/notify";
import { TaskForm } from "@/features/new-application/task-form";
import {
  useSubmitTask,
  useTaskForm,
} from "@/features/new-application/use-start-application";

import { selectNextApproval } from "@/features/approvals/select-next-approval";
import { usePendingApprovals } from "@/features/approvals/use-approvals";

import { SubmittedContent } from "./submitted-content";
import { useCaseAction } from "./use-case-action";
import type { CaseRef } from "./use-case-detail";

/**
 * 案件詳細でその場で操作するための節。
 *
 * 承認は「承認リクエスト一覧から」、下書きの続きは「要対応から」しか辿れず、
 * 詳細を開いた人が同じ操作をするのに一覧へ戻る必要があった。ここに置けば、
 * 通知・検索・全案件モニタなど**どこから来ても操作できる**。
 *
 * 出す条件は backend の `can_complete`（`use-case-action`）だけ。押しても通らない
 * ボタンは出さない。一覧側の入口は残す（一括承認・連続承認は一覧の価値）。
 */
export function CaseActionSection({ caseRef }: { readonly caseRef: CaseRef }) {
  const { t } = useTranslation("common");
  const action = useCaseAction(caseRef);
  const { hash } = useLocation();
  const sectionRef = useRef<HTMLElement>(null);

  // 要対応・通知・起票直後は hash 付きで来る。詳細は情報が多いので、
  // 操作しに来た人にはパネルの位置まで送る（描画されてからでないと効かない）
  // 取得中は data が undefined。パネルが描かれてから送らないと空振りする
  const shouldScroll =
    hash === CASE_ACTION_HASH &&
    action.data !== null &&
    action.data !== undefined;
  useEffect(() => {
    if (!shouldScroll) return;
    sectionRef.current?.scrollIntoView({ block: "start" });
  }, [shouldScroll]);

  const taskRef =
    action.data && !action.data.formMissing
      ? { caseId: caseRef.caseId, taskGuid: action.data.taskGuid }
      : null;
  const form = useTaskForm(taskRef);
  const submit = useSubmitTask();
  // 連続してさばく導線（旧・判定画面の「次へ」）。一覧を開き直さずに次へ行ける
  const pending = usePendingApprovals();

  if (action.isPending) return null;
  if (!action.data) return null;

  const { kind, stepLabel, formMissing } = action.data;
  const next =
    kind === "review"
      ? selectNextApproval(pending.data ?? [], action.data.taskGuid)
      : null;

  return (
    <section
      ref={sectionRef}
      id="case-action"
      aria-label={t("caseDetail.action.title")}
      className="border-primary/40 bg-primary/5 flex flex-col gap-3 rounded-lg border p-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">
          {t("caseDetail.action.title")}
        </h2>
        {stepLabel === null ? null : (
          <span className="text-muted-foreground text-sm">{stepLabel}</span>
        )}
      </div>

      <p className="text-muted-foreground text-sm">
        {t(
          kind === "draft"
            ? "caseDetail.action.draftNote"
            : "caseDetail.action.reviewNote",
        )}
      </p>

      {formMissing ? (
        <p role="alert" className="text-destructive text-sm">
          {t("caseDetail.action.formMissing")}
        </p>
      ) : null}

      {taskRef !== null && form.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : null}

      {/*
        判定には「宣言し忘れた項目」も要る。案件詳細の「この案件の内容」は
        metadata_extraction_paths に宣言したものだけなので、承認のときは
        タスクデータ（起案で入力された値）をそのまま出す。
        下書きは入力フォーム自体が値を持つので出さない（二重になる）。
      */}
      {kind === "review" && form.data ? (
        <SubmittedContent data={form.data.formData} />
      ) : null}

      {taskRef !== null && form.data ? (
        <TaskForm
          schema={form.data.schema}
          uiSchema={form.data.uiSchema}
          // 判定フォームは空から始める（タスクデータには起案の入力値が入っており、
          // そのまま初期値にすると判定と関係ない項目まで提出してしまう）。
          // 下書きは続きを書くので前回の入力を初期値にする
          formData={kind === "draft" ? form.data.formData : {}}
          submitLabel={t(
            kind === "draft"
              ? "caseDetail.action.draft"
              : "caseDetail.action.review",
          )}
          isSubmitting={submit.isPending}
          onSubmit={(formData) =>
            submit.mutate(
              { ref: taskRef, formData },
              {
                // 表示の作法は他の画面と揃える（ページ内に出し分けない）
                onSuccess: () =>
                  notifySuccess(
                    t(kind === "draft" ? "toast.submitted" : "toast.decided"),
                  ),
              },
            )
          }
        />
      ) : null}

      {/*
        承認後の「次へ」。勝手に他の案件へ飛ばさず、残りがあるときだけ出す
        （通知や検索から 1 件だけ見に来た人を連れ去らない）。
      */}
      {submit.isSuccess && kind === "review" && next !== null ? (
        <Button asChild variant="outline" className="w-fit">
          <Link to={caseActionPath(next.caseId, next.processModelId)}>
            {t("caseDetail.action.next", {
              name: next.processModelDisplayName,
            })}
          </Link>
        </Button>
      ) : null}

      {submit.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {t("caseDetail.action.submitFailed")}
        </p>
      ) : null}
    </section>
  );
}
